<template>
  <div class="th-pag" v-if="totalPages > 1">
    <div class="th-pag__info">
      Exibindo de {{ from }} a {{ to }}, total de {{ total }}.
    </div>

    <div class="th-pag__controls" role="navigation" aria-label="Paginação">

      <span
        class="th-pag__btn th-pag__btn--nav"
        :class="{ 'th-pag__btn--disabled': currentPage === 1 }"
        role="button"
        tabindex="0"
        title="Página anterior"
        @click="changePage(currentPage - 1)"
        @keydown.enter.prevent="changePage(currentPage - 1)"
      >‹</span>

      <span
        v-for="page in visiblePages"
        :key="`p-${page}`"
        class="th-pag__btn"
        :class="{
          'th-pag__btn--active': page === currentPage,
          'th-pag__btn--ellipsis': page === '...',
          'th-pag__btn--disabled': page === '...',
        }"
        role="button"
        :tabindex="page === '...' ? -1 : 0"
        :aria-current="page === currentPage ? 'page' : undefined"
        @click="page !== '...' ? changePage(page) : null"
        @keydown.enter.prevent="page !== '...' ? changePage(page) : null"
      >{{ page }}</span>

      <span
        class="th-pag__btn th-pag__btn--nav"
        :class="{ 'th-pag__btn--disabled': currentPage === totalPages }"
        role="button"
        tabindex="0"
        title="Próxima página"
        @click="changePage(currentPage + 1)"
        @keydown.enter.prevent="changePage(currentPage + 1)"
      >›</span>
    </div>

    <div class="th-pag__per-page">
      <label class="th-pag__per-page-label" for="th-pag-per-page">Por página</label>
      <select
        id="th-pag-per-page"
        class="th-pag__per-page-select"
        :value="perPage"
        @change="changePerPage($event.target.value)"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaginationComponent",

  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalPages: {
      type: Number,
      required: true,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 30,
    },
    total: {
      type: Number,
      required: true,
      default: 0,
    },
    from: {
      type: Number,
      default: 0,
    },
    to: {
      type: Number,
      default: 0,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 20, 30, 50, 100],
    },
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 1;

      if (total <= 5) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);

        let start = Math.max(2, current - delta);
        let end = Math.min(total - 1, current + delta);

        if (start > 2) {
          pages.push("...");
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (end < total - 1) {
          pages.push("...");
        }

        pages.push(total);
      }

      return pages;
    },
  },

  methods: {
    changePage(page) {
      if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
        this.$emit("page-changed", page);
      }
    },

    changePerPage(value) {
      this.$emit("per-page-changed", parseInt(value, 10));
    },
  },
};
</script>

<!-- Sem scoped: estilos da lib precisam aplicar no app consumidor sem depender de data-v-* -->
<style>
.th-pag {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 8px 0;
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
}

.th-pag__info {
  flex: 0 1 auto;
  color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.54));
  font-size: 0.85rem;
  line-height: 1.2;
  white-space: nowrap;
}

.th-pag__controls {
  display: inline-flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.th-pag__btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--cor-separador, rgba(0, 0, 0, 0.18));
  border-radius: 6px;
  background: var(--cor-bg, #fff);
  color: var(--cor-fonte, rgba(0, 0, 0, 0.85));
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.th-pag__btn--nav {
  min-width: 32px;
  padding: 0;
  color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.54));
}

.th-pag__btn:hover:not(.th-pag__btn--disabled):not(.th-pag__btn--ellipsis) {
  background: var(--cor-primaria-fraca, rgba(33, 150, 243, 0.08));
  border-color: var(--cor-primaria, #2196f3);
  color: var(--cor-primaria-forte, #1976d2);
}

.th-pag .th-pag__btn.th-pag__btn--active,
.th-pag .th-pag__btn.th-pag__btn--active:hover {
  background: var(--cor-primaria, #2196f3);
  border-color: var(--cor-primaria, #2196f3);
  color: #ffffff !important;
  font-weight: 700;
}

.th-pag .th-pag__btn.th-pag__btn--active:hover:not(.th-pag__btn--disabled) {
  background: var(--cor-primaria-forte, #1976d2);
  border-color: var(--cor-primaria-forte, #1976d2);
  color: #ffffff !important;
}

.th-pag__btn--disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.th-pag__btn--ellipsis {
  min-width: 24px;
  border-color: transparent;
  background: transparent;
  cursor: default;
  color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.54));
}

.th-pag__btn--ellipsis:hover {
  background: transparent;
  border-color: transparent;
  color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.54));
}

.th-pag__per-page {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.th-pag__per-page-label {
  margin: 0;
  font-size: 0.85rem;
  color: var(--cor-fonte-fraca, rgba(0, 0, 0, 0.54));
}

.th-pag__per-page-select {
  height: 32px;
  padding: 0 28px 0 10px;
  border: 1px solid var(--cor-separador, rgba(0, 0, 0, 0.18));
  border-radius: 6px;
  background: var(--cor-bg, #fff);
  color: var(--cor-fonte, rgba(0, 0, 0, 0.85));
  font-size: 0.85rem;
  cursor: pointer;
}

.th-pag__per-page-select:focus {
  outline: none;
  border-color: var(--cor-primaria, #2196f3);
}
</style>
